import React from 'react';
import cn from '@/utils/cn';
import { formatRange } from './dateRanges';
import { checkLabel, formatRate } from './reportParams';
import type { AISafetyReportResponse } from './types';

interface StatProps {
  label: string;
  sub: string;
  value: string;
  warning?: boolean;
}

const cellClass = 'tw-box-border tw-min-w-0 tw-bg-spa-bg-secondary tw-px-5 tw-py-4';

const Stat = ({
  label, sub, value, warning = false,
}: StatProps) => (
  <div className={cellClass}>
    <div
      className={cn(
        'tw-flex tw-items-center tw-gap-1.5 tw-text-[0.6875rem] tw-font-semibold',
        'tw-uppercase tw-tracking-wider tw-text-spa-secondary',
      )}
    >
      {warning && <span aria-hidden="true" className="tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full tw-bg-spa-warning" />}
      {label}
    </div>
    <div className="tw-mt-1.5 tw-text-[1.75rem] tw-font-semibold tw-leading-tight tw-text-spa-heading">{value}</div>
    <div className="tw-mt-1 tw-text-xs tw-text-spa-tertiary">{sub}</div>
  </div>
);

const SkeletonStat = () => (
  <div aria-hidden="true" className={cn(cellClass, 'tw-animate-pulse')}>
    <div className="tw-h-3 tw-w-24 tw-rounded tw-bg-spa-bg-skeleton" />
    <div className="tw-mt-2.5 tw-h-7 tw-w-20 tw-rounded tw-bg-spa-bg-skeleton" />
    <div className="tw-mt-2 tw-h-3 tw-w-28 tw-rounded tw-bg-spa-bg-skeleton-subtle" />
  </div>
);

interface SummaryStripProps {
  from: string;
  report?: AISafetyReportResponse;
  to: string;
}

const wideColumns = (cellCount: number) => {
  if (cellCount >= 4) return 'lg:tw-grid-cols-4';
  if (cellCount === 3) return 'lg:tw-grid-cols-3';
  return 'lg:tw-grid-cols-2';
};

// One raised surface divided into cells: the 1px grid gap shows the container's border colour
// through, so the dividers work at both column counts without nth-child rules.
const SummaryStrip = ({ from, report, to }: SummaryStripProps) => {
  const checks = report ? Object.entries(report.checks) : [];
  const cellCount = report ? checks.length + 2 : 4;

  return (
    <div
      aria-busy={!report}
      aria-label="Summary"
      className={cn(
        'tw-mb-7 tw-grid tw-grid-cols-2 tw-gap-px tw-overflow-hidden tw-rounded-lg',
        'tw-border tw-border-solid tw-border-spa-border-secondary tw-bg-spa-border-secondary',
        wideColumns(cellCount),
      )}
      role="region"
    >
      {report ? (
        <>
          <Stat
            label="Questions screened"
            sub={report.subscriber ? `${report.subscriber.name} · ${formatRange(from, to)}` : formatRange(from, to)}
            value={report.totalSubjects.toLocaleString()}
          />
          {checks.map(([name, check]) => (
            <Stat
              key={name}
              label={checkLabel(name)}
              sub={`${check.count.toLocaleString()} of ${check.total.toLocaleString()} failed`}
              value={formatRate(check.rate)}
            />
          ))}
          <Stat
            label="Errored, unaudited"
            sub={`${report.errors.count.toLocaleString()} of ${report.errors.total.toLocaleString()} `
              + 'could not be checked'}
            value={formatRate(report.errors.rate)}
            warning={report.errors.count > 0}
          />
        </>
      ) : (
        Array.from({ length: cellCount }, (_, index) => <SkeletonStat key={index} />)
      )}
    </div>
  );
};

export default SummaryStrip;
